import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiPlus, FiEdit2, FiBarChart2, FiTrash2, FiCopy, FiExternalLink, FiGrid, FiToggleLeft, FiToggleRight, FiLink, FiEye, FiCheck } from "react-icons/fi";
import { useToast } from "../components/ToastProvider";
import { api } from "../utils/api";
import { getBaseUrl } from "../utils/domain";
import "../styles/Dashboard.css";
import "../styles/BioPages.css";

export default function BioPagesList() {
  const [bioPages, setBioPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const fetchBioPages = useCallback(async () => {
    try {
      const data = await api.getBioPages(1, 50);
      setBioPages(data.bio_pages || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
      addToast("Failed to load bio pages", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    document.title = "Bio Pages - LinkSquire";
    fetchBioPages();
  }, [fetchBioPages]);

  const getFullUrl = (bioPage) => {
    const base = bioPage.domain ? `https://${bioPage.domain}` : getBaseUrl();
    return `${base}/${bioPage.slug}`;
  };

  const copyToClipboard = (url, id, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    addToast("URL copied to clipboard", "success");
    setTimeout(() => setCopiedId(null), 1500);
  };

  const toggleActive = async (bioPage, e) => {
    e.stopPropagation();
    const newState = !bioPage.is_active;
    setBioPages(prev => prev.map(b => b.$id === bioPage.$id ? { ...b, is_active: newState } : b));
    setTogglingId(bioPage.$id);
    try {
      await api.updateBioPage(bioPage.$id, { is_active: newState });
      addToast(newState ? "Bio page activated" : "Bio page deactivated", newState ? "success" : "warning");
    } catch (err) {
      setBioPages(prev => prev.map(b => b.$id === bioPage.$id ? { ...b, is_active: bioPage.is_active } : b));
      addToast("Failed to update bio page", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const deleteBioPage = async (id, title, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) return;
    
    setDeletingId(id);
    try {
      await api.deleteBioPage(id);
      addToast("Bio page deleted", "success");
      setBioPages(bioPages.filter(b => b.$id !== id));
    } catch (err) {
      addToast("Failed to delete bio page", "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Bio Pages</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginTop: 4 }}>
            Create beautiful link-in-bio pages to share multiple links from a single URL.
          </p>
        </div>
        <button className="btn-primary" onClick={() => navigate("/bio/new")}>
          <FiPlus /> Create Bio Page
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading bio pages...</p>
        </div>
      ) : bioPages.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 20px", border: "1px solid var(--border)", borderRadius: "12px", background: "var(--surface)" }}>
          <div style={{ 
            width: 80, 
            height: 80, 
            borderRadius: "50%", 
            background: "var(--primary-light)", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            margin: "0 auto 20px"
          }}>
            <FiGrid size={32} style={{ color: "var(--primary)" }} />
          </div>
          <h3 style={{ marginBottom: "8px", fontSize: "20px", fontWeight: 600 }}>Create your first bio page</h3>
          <p style={{ color: "var(--text-secondary)", marginBottom: "24px", fontSize: "14px", maxWidth: 360, margin: "0 auto 24px" }}>
            Build a beautiful link-in-bio page in minutes. Choose from templates or start from scratch.
          </p>
          <button className="btn-primary" onClick={() => navigate("/bio/new")} style={{ padding: "10px 20px", height: "auto" }}>
            <FiPlus /> Get Started
          </button>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16, fontSize: 14, color: "var(--text-secondary)" }}>
            {total} bio page{total !== 1 ? "s" : ""}
          </div>
          <div className="bio-pages-grid">
            {bioPages.map(bioPage => {
              const fullUrl = getFullUrl(bioPage);
              const linksCount = (bioPage.links || []).filter(l => l.enabled !== false).length;
              
              return (
                <div 
                  key={bioPage.$id} 
                  className="bio-page-card"
                  onClick={() => navigate(`/bio/${bioPage.$id}/edit`)}
                  style={{ cursor: "pointer" }}
                >
                  {/* Mini Preview */}
                  <div className="bio-page-card-preview" style={{ 
                    background: bioPage.theme === "dark" ? "#191919" : "#f7f7f5"
                  }}>
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      padding: 20,
                    }}>
                      {bioPage.avatar_url ? (
                        <img 
                          src={bioPage.avatar_url} 
                          alt=""
                          style={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: "50%", 
                            objectFit: "cover",
                            marginBottom: 8
                          }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div style={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: "50%", 
                          background: bioPage.theme === "dark" ? "#333" : "#ddd",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 16,
                        }}>
                          👤
                        </div>
                      )}
                      <div style={{ 
                        color: bioPage.theme === "dark" ? "#fff" : "#37352f",
                        fontSize: 12,
                        fontWeight: 600,
                        textAlign: "center",
                        marginBottom: 4
                      }}>
                        {bioPage.title}
                      </div>
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: 4,
                        width: "80%"
                      }}>
                        {(bioPage.links || []).slice(0, 2).map((link, i) => (
                          <div 
                            key={i} 
                            style={{
                              background: bioPage.theme === "dark" ? "#fff" : "#37352f",
                              color: bioPage.theme === "dark" ? "#191919" : "#fff",
                              padding: "6px 10px",
                              borderRadius: 4,
                              fontSize: 9,
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            {link.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="bio-page-card-content">
                    <div className="bio-page-card-header">
                      {bioPage.avatar_url ? (
                        <img 
                          src={bioPage.avatar_url} 
                          alt="" 
                          className="bio-page-card-avatar"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <div className="bio-page-card-avatar-placeholder">👤</div>
                      )}
                      <div className="bio-page-card-info">
                        <div className="bio-page-card-title">{bioPage.title}</div>
                        <span className={`bio-page-card-status ${bioPage.is_active ? "active" : "inactive"}`}>
                          {bioPage.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>

                    <a 
                      href={fullUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="bio-page-card-url"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {fullUrl.replace(/^https?:\/\//, "")}
                    </a>

                    <div className="bio-page-card-stats">
                      <div className="bio-page-card-stat">
                        <FiLink size={12} />
                        <span>{linksCount} link{linksCount !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="bio-page-card-stat">
                        <FiEye size={12} />
                        <span>{bioPage.click_count || 0} view{(bioPage.click_count || 0) !== 1 ? "s" : ""}</span>
                      </div>
                    </div>

                    <div className="bio-page-card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={(e) => toggleActive(bioPage, e)} 
                        data-tooltip={bioPage.is_active ? "Deactivate" : "Activate"}
                        disabled={togglingId === bioPage.$id}
                      >
                        {togglingId === bioPage.$id ? <span className="spinner-sm dark" /> : bioPage.is_active ? <FiToggleRight /> : <FiToggleLeft />}
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={(e) => copyToClipboard(fullUrl, bioPage.$id, e)} 
                        data-tooltip={copiedId === bioPage.$id ? "Copied!" : "Copy URL"}
                      >
                        {copiedId === bioPage.$id ? <FiCheck /> : <FiCopy />}
                      </button>
                      <a 
                        href={fullUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn-icon" 
                        data-tooltip="Open"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiExternalLink />
                      </a>
                      <Link 
                        to={`/bio/${bioPage.$id}/analytics`} 
                        className="btn-icon" 
                        data-tooltip="Analytics"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiBarChart2 />
                      </Link>
                      <Link 
                        to={`/bio/${bioPage.$id}/edit`} 
                        className="btn-icon" 
                        data-tooltip="Edit"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiEdit2 />
                      </Link>
                      <button 
                        className="btn-icon btn-icon-danger" 
                        onClick={(e) => deleteBioPage(bioPage.$id, bioPage.title, e)} 
                        data-tooltip="Delete"
                        disabled={deletingId === bioPage.$id}
                      >
                        {deletingId === bioPage.$id ? <span className="spinner-sm dark" /> : <FiTrash2 />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
